import React from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

const AlertasCriticas = ({ alugueisAtrasados = [], fornecedoresVencendo = [] }) => {
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const totalAlugueisAtrasados = alugueisAtrasados.reduce((sum, a) => sum + a.valor, 0);
  const totalFornecedoresVencendo = fornecedoresVencendo.reduce((sum, f) => sum + f.valor, 0);

  return (
    <Stack spacing={2}>
      {/* ALUGUÉIS ATRASADOS */}
      {alugueisAtrasados.length > 0 && (
        <Card sx={{ borderLeft: '4px solid #f44336', backgroundColor: '#ffebee' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ErrorIcon sx={{ color: '#f44336', fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f1f1f' }}>
                  🔴 Aluguéis Atrasados
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {alugueisAtrasados.length} aluguéis • Total: {formatarMoeda(totalAlugueisAtrasados)}
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Dias</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Multa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alugueisAtrasados.slice(0, 5).map((aluguel) => (
                    <TableRow key={aluguel.id} sx={{ '&:hover': { backgroundColor: '#fff9c4' } }}>
                      <TableCell sx={{ color: '#1f1f1f' }}>{aluguel.cliente}</TableCell>
                      <TableCell align="right" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                        {formatarMoeda(aluguel.valor)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${aluguel.diasAtraso}d`}
                          size="small"
                          sx={{
                            backgroundColor: aluguel.diasAtraso > 30 ? '#f44336' : '#ff9800',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#f44336' }}>
                        {formatarMoeda(aluguel.multa || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* FORNECEDORES VENCENDO */}
      {fornecedoresVencendo.length > 0 && (
        <Card sx={{ borderLeft: '4px solid #ff9800', backgroundColor: '#fff3e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f1f1f' }}>
                  ⚠️ Fornecedores Vencendo em 7 Dias
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {fornecedoresVencendo.length} contas • Total: {formatarMoeda(totalFornecedoresVencendo)}
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fornecedor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Vencimento</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Dias</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fornecedoresVencendo.slice(0, 5).map((fornecedor) => (
                    <TableRow key={fornecedor.id} sx={{ '&:hover': { backgroundColor: '#fff9c4' } }}>
                      <TableCell sx={{ color: '#1f1f1f' }}>{fornecedor.fornecedor}</TableCell>
                      <TableCell align="right" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                        {formatarMoeda(fornecedor.valor)}
                      </TableCell>
                      <TableCell align="center">{formatarData(fornecedor.dataVencimento)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${fornecedor.diasParaVencer}d`}
                          size="small"
                          sx={{
                            backgroundColor: fornecedor.diasParaVencer <= 2 ? '#f44336' : '#ff9800',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* SEM ALERTAS */}
      {alugueisAtrasados.length === 0 && fornecedoresVencendo.length === 0 && (
        <Alert severity="success" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}>
          ✅ Nenhum alerta crítico! Tudo está em dia.
        </Alert>
      )}
    </Stack>
  );
};

export default AlertasCriticas;
